import { SectionNotAvailable } from './SectionNotAvilable';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ProjectTeamSideNav } from './ProjectTeamSideNav';
import { ProjectTeamMemberDetail } from './ProjectTeamMemberDetail';
import { BASE_STYLE } from './style';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { useProcessedTeamData, useTeamNavigation } from './utils';

/**
 * Main component that displays project team members with navigation between different professionals
 */
export const ProjectTeam = ({
    projectID,
    openEnquiryModal,
}: {
    projectID: number;
    openEnquiryModal: () => void;
}) => {
    const { projectTeamMembers, project } = useLoaderData<typeof loader>();
    const [activeTab, setActiveTab] = useState<string>('');
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
    // Use ref instead of state to track navigation - prevents rerender cycles
    const isNavigatingRef = useRef(false);

    // Get the professional's country from the project data
    const professionalCountry = project?.Professional?.Country;

    // Process team data using custom hook
    const {
        allTeamMembers,
        categoryTeamMembers,
        availableCategories,
        getCategoryMembers,
        findCategoryForMember,
    } = useProcessedTeamData(projectTeamMembers);

    // Get members of current active category
    const currentCategoryMembers = useMemo(
        () => getCategoryMembers(activeTab),
        [getCategoryMembers, activeTab]
    );

    // Get current team member to display
    const currentTeamMember = useMemo(() => {
        if (currentCategoryMembers.length === 0) return null;
        return currentCategoryMembers[activeCategoryIndex];
    }, [currentCategoryMembers, activeCategoryIndex]);

    // Navigation functionality between team members
    const { handleNavigation } = useTeamNavigation(
        activeTab,
        availableCategories,
        categoryTeamMembers,
        setActiveTab,
        activeCategoryIndex,
        setActiveCategoryIndex
    );

    // Set initial active tab
    useEffect(() => {
        if (!activeTab && availableCategories.length > 0) {
            setActiveTab(availableCategories[0] || '');
            setActiveCategoryIndex(0);
        }
    }, [availableCategories, activeTab]);

    // Reset index when category changes (but not during navigation)
    useEffect(() => {
        // Skip resetting if we're in the middle of a navigation action
        if (isNavigatingRef.current) {
            isNavigatingRef.current = false;
            return;
        }

        // Only reset the index when tab changes and we're not navigating
        setActiveCategoryIndex(0);
    }, [activeTab]);

    const handlePrev = useCallback(() => {
        isNavigatingRef.current = true;
        handleNavigation('prev', allTeamMembers.length);
    }, [handleNavigation, allTeamMembers.length]);

    const handleNext = useCallback(() => {
        isNavigatingRef.current = true;
        handleNavigation('next', allTeamMembers.length);
    }, [handleNavigation, allTeamMembers.length]);

    // Display message when no team members are available
    if (allTeamMembers.length === 0) {
        return (
            <SectionNotAvailable
                description={
                    <>
                        No team members info available for this project.
                        <br />
                        Request more information from this professional.
                    </>
                }
                enquiryButtonText="Request Project Team Info"
                openEnquiryModal={openEnquiryModal}
            />
        );
    }

    if (!currentTeamMember) return null;

    // Get category data for current team member
    const currentCategory = findCategoryForMember(currentTeamMember);

    if (!currentCategory) return null;

    // Show navigation buttons when there are multiple team members
    const showNavigation = allTeamMembers.length > 1;

    return (
        <div
            className={`w-full ${BASE_STYLE.TOP_MARGIN} ${BASE_STYLE.BOTTOM_MARGIN}`}
        >
            <div className="mt-15 flex w-full">
                <ProjectTeamSideNav
                    teamList={availableCategories}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    setCurrentIndex={() => setActiveCategoryIndex(0)}
                    teamData={projectTeamMembers}
                    currentTeamMember={currentTeamMember}
                />

                <div className={BASE_STYLE.CONTENT_WIDTH}>
                    <ProjectTeamMemberDetail
                        currentMember={currentCategory}
                        currentTeamMember={currentTeamMember}
                        teamMembers={currentCategoryMembers}
                        handlePrev={handlePrev}
                        handleNext={handleNext}
                        projectID={projectID}
                        showNavigation={showNavigation}
                        professionalCountry={professionalCountry}
                    />
                </div>
            </div>
        </div>
    );
};
